import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, ArrowUpRight, BookOpen, MapPin, UsersRound } from "lucide-react";
import { api } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { homeGalleryForMinute } from "../data/homeGallery";

const collageSlots = ["lead", "left", "center", "right", "low"];

export default function Home({ authenticated, canContribute }) {
  const statsLoader = useCallback(() => api("/api/site"), []);
  const { data: stats } = useApi(statsLoader, []);
  const [galleryMinute] = useState(() => Math.floor(Date.now() / 60000));
  const galleryPhotos = homeGalleryForMinute(galleryMinute);

  return (
    <>
      <section className="hero">
        <div className="hero__year" aria-hidden="true">95</div>
        <div className="hero__copy reveal">
          <p className="kicker">Mar Athanasius College of Engineering · 1991–1995</p>
          <h1>
            We graduated.
            <em>The story didn’t.</em>
          </h1>
          <p className="hero__lede">
            A living yearbook for the people, places, and reunions that keep the MACE ’95 class together.
          </p>
          <div className="button-row">
            <Link className="button button--coral" to="/reunions">Enter the reunion archive <ArrowRight aria-hidden="true" /></Link>
            <Link className="button button--ghost" to="/members">{authenticated ? "Open member room" : "Find a classmate"}</Link>
          </div>
        </div>
        <div className="hero__collage reveal reveal--delay" aria-label="Five photographs selected from 100 reunion favorites">
          {galleryPhotos.map((photo, index) => (
            <Link
              className={`hero-photo hero-photo--${collageSlots[index]}`}
              key={photo.id}
              to={photo.reunion}
              aria-label={`Open ${photo.event}: ${photo.location}, ${photo.year}`}
            >
              <img
                src={photo.src}
                alt={photo.alt}
                loading={index < 2 ? "eager" : "lazy"}
                fetchPriority={index === 0 ? "high" : "auto"}
              />
              <span className="hero-photo__meta">
                <strong>{photo.year}</strong>
                <span>{photo.location}</span>
              </span>
            </Link>
          ))}
          <div className="hero__stamp" aria-hidden="true">100 favorites<br />5 at a time</div>
          <p className="hero__edition"><span aria-hidden="true" /> A new reunion edit every minute</p>
        </div>
        <a className="hero__scroll" href="#class-story"><ArrowDown aria-hidden="true" /> Scroll through the years</a>
      </section>

      <section id="class-story" className="class-story section-wrap">
        <div className="section-heading">
          <p className="kicker">The class, by the numbers</p>
          <h2>Eight branches.<br />One long conversation.</h2>
        </div>
        <div className="stat-ledger">
          <div><strong>{stats?.memberCount || 304}</strong><span>names recovered from the original directory</span></div>
          <div><strong>8</strong><span>class sections represented</span></div>
          <div><strong>{stats?.archivePhotoCount || 333}</strong><span>photographs in the first digital reunion archive</span></div>
          <div><strong>{stats?.countryCount || 18}+</strong><span>countries called home over the years</span></div>
        </div>
      </section>

      <section className="then-now">
        <div className="then-now__image">
          <img src="/media/brand/mace-front-legacy.webp" alt="Panoramic view of the MACE campus in Kothamangalam" />
          <span className="image-index">KOTHAMANGALAM / 001</span>
        </div>
        <div className="then-now__copy section-wrap">
          <p className="kicker">From one campus to everywhere</p>
          <h2>The road from Kothamangalam now reaches every corner of the world.</h2>
          <p>
            Find out where classmates are today, share what life looks like now, and use one familiar campus as the starting point for new conversations, local meetups, and the reunion still ahead.
          </p>
          <Link className="text-link" to="/members">See where the class is now <ArrowRight aria-hidden="true" /></Link>
        </div>
      </section>

      <section className="featured-reunion section-wrap">
        <div className="featured-reunion__meta">
          <p className="kicker">From the archive · December 2005</p>
          <h2>Two days in Dubai.<br />Ten years disappeared.</h2>
          <p>
            Formal introductions gave way to old jokes, a desert safari, family portraits, music, and the unmistakable feeling that everyone had simply picked up where they left off.
          </p>
          <Link className="button button--ink" to="/reunions/reunion-2005">Open the 2005 story <BookOpen aria-hidden="true" /></Link>
        </div>
        <div className="featured-reunion__strip" aria-label="Selected photographs from the 2005 reunion">
          {["32860015.JPG", "DSC02458.JPG", "95reunion%20022.jpg"].map((name, index) => (
            <figure key={name} style={{ "--i": index }}>
              <img src={`/media/reunions/2005/${name}`} alt="A moment from the MACE95 2005 Dubai reunion" />
            </figure>
          ))}
        </div>
      </section>

      <section className="home-actions section-wrap">
        <article className="action-panel action-panel--coral">
          <UsersRound aria-hidden="true" />
          <p className="kicker">Keep your chapter current</p>
          <h2>Update the person your classmates know now.</h2>
          <p>Add your current city, work, LinkedIn profile, and the best way to reach you. You choose which details are shared.</p>
          <Link className="text-link" to="/members">{authenticated && !canContribute ? "View class directory" : "Update my profile"} <ArrowRight aria-hidden="true" /></Link>
        </article>
        <article className="action-panel action-panel--blue">
          <MapPin aria-hidden="true" />
          <p className="kicker">No milestone required</p>
          <h2>Make a reunion page for your city.</h2>
          <p>Pick a date, invite the local circle, and turn everyone’s photographs into one elegant shared story.</p>
          <Link className="text-link" to={authenticated && !canContribute ? "/reunions" : "/reunions?create=1"}>{authenticated && !canContribute ? "View reunion pages" : "Start a local reunion"} <ArrowRight aria-hidden="true" /></Link>
        </article>
      </section>

      <section className="college-ribbon">
        <div>
          <p className="kicker">Back to where it began</p>
          <h2>News, contacts, alumni officers, and the campus today.</h2>
        </div>
        <Link className="button button--cream" to="/college">Visit the college page <ArrowUpRight aria-hidden="true" /></Link>
      </section>
    </>
  );
}
